import React, { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Fade from '@mui/material/Fade'
import { alpha, keyframes } from '@mui/material/styles'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { Link as RouterLink } from 'react-router-dom'

const rotate = keyframes`
    0% {
        transform: rotate(0deg) translateZ(0);
    }
    100% {
        transform: rotate(360deg) translateZ(0);
    }
`

const float = keyframes`
    0% {
        transform: translateY(0);
        opacity: 1;
    }
    50% {
        transform: translateY(-10px);
        opacity: 0.85;
    }
    100% {
        transform: translateY(0);
        opacity: 1;
    }
`

const pulse = keyframes`
    0% {
        transform: scale(1);
        opacity: 1;
    }
    50% {
        transform: scale(1.35);
        opacity: 0.6;
    }
    100% {
        transform: scale(1);
        opacity: 1;
    }
`

export default function TitleSection () {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const id = setTimeout(() => setIsVisible(true), 120)
    return () => clearTimeout(id)
  }, [])

  return (<Box
    sx={{
      position:                                  'relative',
      minHeight:                                 '90vh',
      display:                                   'flex',
      alignItems:                                'center',
      overflow:                                  'hidden',
      bgcolor:                                   'rgba(212,200,186,0.56)',
      '&::before':                               {
        content:    '""',
        position:   'absolute',
        inset:      0,
        background: 'radial-gradient(circle at 40% 70%,rgba(68,19,2,0.20) 0%, transparent 50%)'
      },
      '@media (prefers-reduced-motion: reduce)': {
        '& *': {
          animation: 'none !important', transition: 'none !important'
        }
      }
    }}
   >
     <Box
      sx={{
        position:    'absolute',
        top:         0,
        right:       0,
        width:       { xs: '100%', md: '60%' },
        height:      '100%',
        background:  `linear-gradient(137deg,rgba(100, 84, 82, 1) 0%, rgba(141, 113, 99, 1) 46%, rgba(193, 149, 120, 1) 100%)`,
        clipPath:    { xs: 'none', md: 'polygon(15% 0%, 100% 0%, 100% 100%, 0% 100%)' },
        opacity:     0.7,
        zIndex:      0,
        overflow:    'visible',
        '&::before': {
          content:    '""',
          position:   'absolute',
          inset:      0,
          background: 'radial-gradient(circle at 20% 50%,rgba(68,19,2,0.20) 0%, transparent 50%)'
        }
      }}
     />

     <Box
      sx={{
        position:   'absolute',
        top:        '20%',
        right:      { xs: '5%', md: '15%' },
        width:      '2px',
        height:     '200px',
        background: 'linear-gradient(180deg, transparent, rgba(97,79,51,0.2), transparent)',
        display:    { xs: 'none', md: 'block' },
        zIndex:     1
      }}
     />

     <Box
      sx={{
        position:   'absolute',
        bottom:     '15%',
        left:       { xs: '5%', md: '10%' },
        width:      '2px',
        height:     '150px',
        background: 'linear-gradient(180deg, transparent, rgba(21,15,6,0.15), transparent)',
        transform:  'rotate(30deg)',
        zIndex:     1
      }}
     />

     <Box
      sx={{
        position:        'absolute',
        bottom:          '10%',
        right:           { xs: '5%', md: '10%' },
        width:           { xs: '260px', md: '360px' },
        height:          { xs: '260px', md: '360px' },
        border:          '2px solid rgba(68,19,2,0.27)',
        boxShadow:       '0 0 10px rgba(97,79,51,0.2)',
        borderRadius:    '20%',
        zIndex:          3,
        color:           'rgba(75,49,30,0.2)',
        display:         'block',
        transformOrigin: '50% 50%',
        willChange:      'transform',
        pointerEvents:   'none',
        animation:       `${rotate} 12s linear infinite`,
        '&::after':      {
          content:         '""',
          position:        'absolute',
          inset:           '20% 24% auto auto',
          width:           '82%',
          aspectRatio:     '1 / 1',
          border:          '2px solid #daa520',
          borderRadius:    '12px',
          boxSizing:       'border-box',
          pointerEvents:   'none',
          transformOrigin: '50% 50%',
          animation:       `${rotate} 10s linear infinite reverse`
        }
      }}
     />

     <Box
      sx={{
        position:     'absolute',
        top:          '25%',
        left:         { xs: '5%', md: '8%' },
        width:        '10px',
        height:       '10px',
        bgcolor:      (theme) => alpha('#614f33', 0.22),
        borderRadius: '50%',
        animation:    `${float} 5.6s cubic-bezier(.4,.0,.2,1) infinite`,
        zIndex:       3,
        willChange:   'transform, opacity'
      }}
     />

     <Box
      sx={{
        position:     'absolute',
        bottom:       '33%',
        left:         { xs: '10%', md: '15%' },
        boxShadow:    '0 0 10px rgba(75,49,30,0.2)',
        width:        '14px',
        height:       '14px',
        bgcolor:      (theme) => alpha('#614f33', 0.16),
        borderRadius: '50%',
        animation:    `${float} 3.8s cubic-bezier(.4,.0,.2,1) infinite reverse`,
        zIndex:       3,
        willChange:   'transform, opacity'
      }}
     />


     <Container
      maxWidth="xl"
      sx={{
        position: 'relative', zIndex: 4, px: { xs: 3, sm: 4, md: 6 }
      }}
     >
       <Grid container spacing={2} alignItems="center">
         <Grid item xs={12} md={8} lg={6}>
           <Fade in={isVisible} timeout={800}>
             <Box>
               <Box
                sx={{
                  width: 80, height: 4, background: 'linear-gradient(90deg, #614f33, #c4a484)', mb: 4, borderRadius: 2
                }}
               />
               <Typography
                variant="h1"
                sx={{
                  fontSize:      { xs: '2.5rem', sm: '3.5rem', md: '4.5rem', lg: '5.5rem' },
                  fontWeight:    300,
                  letterSpacing: '-0.02em',
                  color:         '#1a1a1a',
                  mb:            2,
                  fontFamily:    '"Cormorant Garamond", serif',
                  lineHeight:    1.1
                }}
               >
                 Transformative
                 <Typography
                  component="span"
                  variant="h1"
                  sx={{
                    color:      '#614f33',
                    display:    'block',
                    fontStyle:  'italic',
                    fontSize:   { xs: '2rem', sm: '3rem', md: '4rem', lg: '5rem' },
                    fontFamily: '"Cormorant Garamond", serif',
                    lineHeight: 1.1,
                    mt:         1,
                    position:   'relative',
                    '&::after': {
                      content:    '""',
                      display:    'inline-block',
                      position:   'absolute',
                      bottom:     '10px',
                      left:       0,
                      width:      '100%',
                      height:     '2px',
                      background: 'linear-gradient(90deg, transparent, #614f33, transparent)',
                      opacity:    0.3
                    }
                  }}
                 >
                   Design Services
                 </Typography>
               </Typography>

               <Typography
                variant="h6"
                sx={{
                  color: '#666', mb: 5, lineHeight: 1.6, fontSize: '1.1rem', maxWidth: 500
                }}
               >
                 Comprehensive design services tailored to your unique vision and lifestyle.
               </Typography>

               <Button
                component={RouterLink}
                to="/contact"
                variant="outlined"
                size="large"
                endIcon={<ArrowForwardIcon/>}
                sx={{
                  borderColor:   '#352d18',
                  color:         '#614f33',
                  borderRadius:  '28px',
                  py:            1,
                  fontSize:      '0.9rem',
                  textTransform: 'none',
                  '&:hover':     {
                    bgcolor: (theme) => alpha('#3c3420', 0.08)
                  }
                }}
               >
                 Begin Your Journey
               </Button>
             </Box>
           </Fade>
         </Grid>
       </Grid>
     </Container>
   </Box>)
}
